import React, { useEffect, useState, useRef, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import {
  ArrowLeft,
  Save,
  RotateCw,
  Upload,
  ShieldCheck,
  CheckCircle2,
  ExternalLink,
} from 'lucide-react';
import { toast } from 'sonner';

import { PageHeader } from '@/components/common/PageHeader';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  ROUTES,
  DYNAMIC_APPTYPE_OPTIONS,
  getUserTypesByAppType,
} from '@/utils/constants';
import { cn } from '@/lib/utils';
import { moduleDocAiSchema, type ModuleDocAiSchema } from './schema';
import {
  useCreateModuleDocAi,
  useUpdateModuleDocAi,
  useGetModuleDocAiById,
  useUploadModuleDocAiFile,
} from './hooks/useModuleDocAi';

export const AddEditModuleDocAiPage = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const fileInputRef = useRef<HTMLInputElement>(null);
  const isEdit = !!id;

  const [fileName, setFileName] = useState<string | null>(null);
  const [existingFilePath, setExistingFilePath] = useState<string | null>(null);

  const { data: response } = useGetModuleDocAiById(id);
  const { mutate: createDoc, isPending: isCreating } = useCreateModuleDocAi();
  const { mutate: updateDoc, isPending: isUpdating } = useUpdateModuleDocAi();
  const { mutate: uploadFile, isPending: isUploading } = useUploadModuleDocAiFile();

  const form = useForm<ModuleDocAiSchema>({
    resolver: zodResolver(moduleDocAiSchema),
    defaultValues: {
      appType: '',
      userType: '',
      fileStorageId: '',
      moduleName: '',
      moduleCode: '',
    },
  });

  const watchedAppType = form.watch('appType');
  const availableUserTypes = useMemo(() => {
    if (!watchedAppType) return [];
    return getUserTypesByAppType(watchedAppType);
  }, [watchedAppType]);

  // Load existing details if in Edit mode
  useEffect(() => {
    if (isEdit && response?.data) {
      const data = response.data;

      // Manually set values to guarantee React Hook Form triggers re-render
      form.setValue('appType', data.appType || '', { shouldValidate: true });
      form.setValue('userType', data.userType || '', { shouldValidate: true });
      form.setValue('fileStorageId', data.fileStorageId || '', { shouldValidate: true });
      form.setValue('moduleName', data.moduleName || '', { shouldValidate: true });
      form.setValue('moduleCode', data.moduleCode || '', { shouldValidate: true });

      if (data.filePath) {
        setExistingFilePath(data.filePath);
        try {
          const urlObj = new URL(data.filePath);
          const pathname = urlObj.pathname;
          // Extract filename from URL or default to a clean label
          const filename = pathname.substring(pathname.lastIndexOf('/') + 1);
          setFileName(filename ? decodeURIComponent(filename) : 'Uploaded Document');
        } catch {
          setFileName('Uploaded Document');
        }
      } else {
        setFileName(`File ID: ${data.fileStorageId}`);
      }
    }
  }, [isEdit, response, form]);

  // Watch moduleName to auto-generate moduleCode in uppercase snake_case
  const moduleName = form.watch('moduleName');
  const { dirtyFields } = form.formState;

  useEffect(() => {
    // Only auto-generate if we are adding a new entry OR if moduleName was manually edited
    if (moduleName && (!isEdit || dirtyFields.moduleName)) {
      const code = moduleName
        .toUpperCase()
        .replace(/\s+/g, '_')
        .replace(/[^A-Z0-9_]/g, ''); // alphanumeric and underscores only
      form.setValue('moduleCode', code, { shouldValidate: true });
    }
  }, [moduleName, dirtyFields.moduleName, isEdit, form]);

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const formData = new FormData();
    formData.append('files', file);

    uploadFile(formData, {
      onSuccess: (res) => {
        if (res.status && res.data?.[0]?.id) {
          form.setValue('fileStorageId', res.data[0].id, { shouldValidate: true });
          setFileName(file.name);
          setExistingFilePath(null); // Clear old link since new file is uploaded
          toast.success('File uploaded successfully');
        }
      },
    });
  };

  const onSubmit = (values: ModuleDocAiSchema) => {
    const payload = {
      userType: values.userType,
      appType: values.appType,
      fileStorageId: values.fileStorageId,
      moduleName: values.moduleName,
      moduleCode: values.moduleCode,
    };

    if (isEdit) {
      updateDoc(
        { id: id!, payload },
        {
          onSuccess: () => navigate(ROUTES.MODULE_DOC_AI),
        },
      );
    } else {
      createDoc(payload, {
        onSuccess: () => navigate(ROUTES.MODULE_DOC_AI),
      });
    }
  };

  const isPending = isCreating || isUpdating || isUploading;

  return (
    <div className="w-full  py-6 space-y-6 max-w-2xl mx-auto pb-12 animate-fade-in">
      <PageHeader
        title={isEdit ? 'Edit Module Doc AI' : 'Add Module Doc AI'}
        subtitle={
          isEdit
            ? 'Update existing module configuration'
            : 'Create a new module configuration and map it to assets.'
        }
      >
        <div className="flex flex-wrap gap-2 w-full sm:w-auto">
          <Button
            variant="outline"
            size="sm"
            onClick={() => navigate(ROUTES.MODULE_DOC_AI)}
            className="h-9 w-full sm:w-auto justify-center"
          >
            <ArrowLeft size={16} className="mr-2" />
            Back
          </Button>
        </div>
      </PageHeader>

      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          <Card className="border-border/60 shadow-sm overflow-hidden">
            <CardHeader className="bg-muted/30 border-b border-border/60 px-6 py-4">
              <CardTitle className="text-lg font-bold flex items-center gap-2 text-primary">
                <ShieldCheck size={20} />
                Module Parameters
              </CardTitle>
            </CardHeader>
            <CardContent className=" space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField
                  control={form.control}
                  name="moduleName"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                        Module Name
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          placeholder="e.g. Vendor Management"
                          className="h-11 font-medium"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="moduleCode"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                        Module Code (Auto-generated)
                      </FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          disabled
                          placeholder="AUTO_GENERATED_CODE"
                          className="h-11 font-mono font-bold bg-muted/50 cursor-not-allowed"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <FormField
                  control={form.control}
                  name="appType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                        App Type
                      </FormLabel>
                      <Select
                        onValueChange={(val) => {
                          field.onChange(val);
                          const typesForApp = getUserTypesByAppType(val);
                          const currentU = form.getValues('userType');
                          if (!typesForApp.some((t) => t.value === currentU)) {
                            form.setValue('userType', typesForApp[0]?.value || '', { shouldValidate: true });
                          }
                        }}
                        value={field.value}
                      >
                        <FormControl>
                          <SelectTrigger className="h-11 font-medium">
                            <SelectValue placeholder="Select app type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent className="max-h-72">
                          {DYNAMIC_APPTYPE_OPTIONS.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value} className="font-medium">
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="userType"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                        User Type
                      </FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        value={field.value}
                        disabled={!watchedAppType}
                      >
                        <FormControl>
                          <SelectTrigger className="h-11 font-medium">
                            <SelectValue
                              placeholder={
                                !watchedAppType ? 'Select app type first' : 'Select user type'
                              }
                            />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent className="max-h-72">
                          {availableUserTypes.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value} className="font-medium">
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="fileStorageId"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel className="text-xs font-bold uppercase tracking-wider text-muted-foreground">
                      Upload Document
                    </FormLabel>
                    <FormControl>
                      <div
                        onClick={() => !isPending && fileInputRef.current?.click()}
                        className={cn(
                          'relative cursor-pointer aspect-[21/9] rounded-xl border-2 border-dashed transition-all flex flex-col items-center justify-center p-6 text-center select-none',
                          field.value
                            ? 'border-primary/40 bg-primary/5'
                            : 'border-muted-foreground/20 hover:border-primary/40 hover:bg-muted/50',
                          isPending && 'opacity-50 cursor-wait',
                        )}
                      >
                        {field.value ? (
                          <div className="flex flex-col items-center gap-2">
                            <div className="h-12 w-12 rounded-full bg-primary/20 flex items-center justify-center text-primary animate-scale-in">
                              <CheckCircle2 size={24} />
                            </div>
                            <div className="space-y-1">
                              <p className="text-sm font-bold text-foreground">
                                Document Uploaded Successfully
                              </p>
                              <p className="text-xs text-muted-foreground truncate max-w-[350px]">
                                {fileName}
                              </p>
                            </div>
                            <div className="flex items-center gap-2 mt-2">
                              {existingFilePath && (
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  className="h-8 text-[10px] font-bold uppercase tracking-widest"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    window.open(existingFilePath, '_blank', 'noopener,noreferrer');
                                  }}
                                >
                                  <ExternalLink size={12} className="mr-1" />
                                  View Current
                                </Button>
                              )}
                              <Button
                                type="button"
                                variant="outline"
                                size="sm"
                                className="h-8 text-[10px] font-bold uppercase tracking-widest"
                              >
                                Change File
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex flex-col items-center text-muted-foreground gap-3">
                            <div className="h-12 w-12 rounded-full bg-muted flex items-center justify-center">
                              <Upload size={24} />
                            </div>
                            <div className="space-y-1">
                              <p className="text-sm font-bold text-foreground">
                                Click to upload document
                              </p>
                              <p className="text-[10px] uppercase tracking-widest font-medium opacity-60">
                                PDF, Images or Files (Max 10MB)
                              </p>
                            </div>
                          </div>
                        )}
                        <input
                          type="file"
                          ref={fileInputRef}
                          onChange={handleFileChange}
                          className="hidden"
                          accept=".pdf,image/*"
                        />
                      </div>
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </CardContent>
          </Card>

          <footer className="flex flex-col sm:flex-row items-center justify-end gap-3 pt-4">
            <Button
              type="button"
              variant="ghost"
              onClick={() => navigate(ROUTES.MODULE_DOC_AI)}
              disabled={isPending}
              className="font-bold text-xs uppercase tracking-widest w-full sm:w-auto justify-center"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isPending}
              className="bg-primary hover:bg-primary/90 text-white min-w-[140px] font-bold text-xs uppercase tracking-widest h-11 shadow-lg shadow-primary/20 w-full sm:w-auto justify-center"
            >
              {isPending ? (
                <>
                  <RotateCw size={16} className="mr-2 animate-spin" />
                  Saving...
                </>
              ) : (
                <>
                  <Save size={16} className="mr-2" />
                  {isEdit ? 'Save Changes' : 'Create Module'}
                </>
              )}
            </Button>
          </footer>
        </form>
      </Form>
    </div>
  );
};

export default AddEditModuleDocAiPage;
